import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";

/** Escapa para HTML */
function escapeHtml(str = "") {
  return str
    .replaceAll(/&/g, "&amp;")
    .replaceAll(/</g, "&lt;")
    .replaceAll(/>/g, "&gt;")
    .replaceAll(/"/g, "&quot;")
    .replaceAll(/'/g, "&#039;");
}

/** Divide em tokens alfanuméricos */
function tokenize(str = "") {
  return (str.match(/[A-Za-zÀ-ÿ0-9']+/g) || []).map((w) => w.trim());
}

/** Seleciona até N tokens "significativos" (>=3 chars) preservando ordem */
function keyTokens(str, max = 6) {
  const toks = tokenize(str).filter((t) => t.length >= 3);
  if (toks.length <= max) return toks;
  // pega alguns do início e do fim para manter contexto
  const headCount = Math.ceil(max / 2);
  const tailCount = max - headCount;
  return [...toks.slice(0, headCount), ...toks.slice(-tailCount)];
}

/** Faz highlight de [start,end) no texto original, preservando pontuação */
function highlightByRange(text = "", start = -1, end = -1) {
  if (start < 0 || end <= start) return escapeHtml(text);
  const before = escapeHtml(text.slice(0, start));
  const middle = escapeHtml(text.slice(start, end));
  const after = escapeHtml(text.slice(end));
  return `${before}<mark id="__highlight__">${middle}</mark>${after}`;
}

/** Heurística de busca: tenta exato, depois regex tolerante, depois chunk central */
function findFuzzyRange(text = "", sentence = "") {
  if (!text || !sentence) return null;

  // 1) exato (case sensitive)
  let idx = text.indexOf(sentence);
  if (idx !== -1) return { start: idx, end: idx + sentence.length };

  // 2) exato (case-insensitive)
  const lowerText = text.toLowerCase();
  const lowerSent = sentence.toLowerCase();
  idx = lowerText.indexOf(lowerSent);
  if (idx !== -1) return { start: idx, end: idx + sentence.length };

  // 3) regex tolerante usando tokens-chave espaçados por até 100 chars (.* não pega \n no JS padrão; usamos [\s\S])
  const tokens = keyTokens(sentence, 6);
  if (tokens.length >= 2) {
    const pattern = tokens.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("[\\s\\S]{0,100}");
    try {
      const re = new RegExp(pattern, "i");
      const m = re.exec(text);
      if (m) {
        return { start: m.index, end: m.index + m[0].length };
      }
    } catch {
      /* ignore */
    }
  }

  // 4) tenta um chunk central da sentença (8 palavras) em case-insensitive
  const words = tokenize(sentence);
  if (words.length >= 4) {
    const mid = Math.max(0, Math.floor(words.length / 2) - 4);
    const snippet = words.slice(mid, mid + 8).join(" ");
    const i2 = lowerText.indexOf(snippet.toLowerCase());
    if (i2 !== -1) return { start: i2, end: i2 + snippet.length };
  }

  return null;
}

const ScriptEmotionPartModal = ({
  scriptTitle,
  partText,
  partNumber,
  dopamineInfo,
  visible,
  onHide,
}) => {
  const [highlightSentence, setHighlightSentence] = useState("");
  const [fallbackNote, setFallbackNote] = useState(""); // mostra quando usamos fuzzy
  const preRef = useRef(null);

  useEffect(() => {
    setHighlightSentence("");
    setFallbackNote("");
  }, [partText, partNumber, visible]);

  const dopamineList = useMemo(() => {
    if (!dopamineInfo?.comment) return [];
    try {
      const parsed = JSON.parse(dopamineInfo.comment);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [dopamineInfo]);

  const renderedHtml = useMemo(() => {
    if (!partText) return "No script part available (to be backfilled).";
    if (!highlightSentence) return escapeHtml(partText);

    // tenta localizar a sentença de forma fuzzy no texto original
    const range = findFuzzyRange(partText, highlightSentence);
    if (range) {
      // se não foi pelo modo 1/2, adiciona observação
      const wasExact =
        partText.indexOf(highlightSentence) !== -1 ||
        partText.toLowerCase().indexOf(highlightSentence.toLowerCase()) !== -1;
      if (!wasExact && !fallbackNote) {
        setFallbackNote(" (approximate match)");
      }
      return highlightByRange(partText, range.start, range.end);
    } else {
      // não achou: apenas mostra texto normal e limpa nota
      if (fallbackNote) setFallbackNote("");
      return escapeHtml(partText);
    }
    // eslint-disable-next-line
  }, [partText, highlightSentence]);

  // após render, rola / seleciona
  useEffect(() => {
    if (!highlightSentence) return;
    const el = document.getElementById("__highlight__");
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const sel = window.getSelection();
      const range = document.createRange();
      range.selectNodeContents(el);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [renderedHtml, highlightSentence]);

  const handlePeakClick = useCallback((sentence) => {
    if (!sentence) return;
    setHighlightSentence(sentence);
  }, []);

  return (
    <Dialog
      header={`Part ${partNumber}${scriptTitle ? ` — ${scriptTitle}` : ""}`}
      visible={visible}
      style={{ width: "60vw", maxWidth: 900 }}
      onHide={onHide}
    >
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "1rem" }}>
        <section>
          <h4 style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span>Script excerpt</span>
            {highlightSentence && (
              <small style={{ opacity: 0.7 }}>
                Highlight for: “{highlightSentence}”
                <span style={{ opacity: 0.7 }}>{fallbackNote}</span>
              </small>
            )}
          </h4>
          <pre
            ref={preRef}
            style={{
              whiteSpace: "pre-wrap",
              maxHeight: 320,
              overflow: "auto",
              margin: 0,
              lineHeight: 1.5,
            }}
            dangerouslySetInnerHTML={{
              __html:
                renderedHtml || "No script part available (to be backfilled).",
            }}
          />
        </section>

        <section>
          <h4>Dopamine peaks</h4>
          <p style={{ marginTop: 0 }}>
            <strong>Average score:</strong>{" "}
            {dopamineInfo?.score != null
              ? Number(dopamineInfo.score).toFixed(1)
              : "—"}
          </p>

          {dopamineList.length > 0 ? (
            <div
              style={{
                border: "1px solid #eee",
                borderRadius: 8,
                padding: 12,
                maxHeight: 260,
                overflow: "auto",
              }}
            >
              {dopamineList.map((d, idx) => (
                <div
                  key={idx}
                  onClick={() => handlePeakClick(d.sentence)}
                  style={{
                    marginBottom: 12,
                    padding: "6px 8px",
                    borderRadius: 6,
                    cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f7f7f7")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                  title="Click to highlight this sentence above"
                >
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    <strong>Peak #{idx + 1}</strong>{" "}
                    {typeof d.score === "number" ? `— score: ${d.score}` : ""}
                  </div>
                  <div style={{ fontStyle: "italic", margin: "6px 0" }}>
                    “{d.sentence || "—"}”
                  </div>
                  <div style={{ fontSize: 13 }}>{d.reason || "—"}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ opacity: 0.7 }}>No dopamine peaks for this part.</div>
          )}
        </section>
      </div>
    </Dialog>
  );
};

export default ScriptEmotionPartModal;
